<script setup lang="ts">
  import { onMounted, watch } from 'vue';
  // Components
  import { IconImage, SelectInput, ColoredButton } from '@/shared/ui';
  // Icons
  import LinkIcon from '@/shared/assets/icons/LinkIcon.svg';
  import UpandDownIcon from '@/shared/assets/icons/UpandDown.svg';
  import PlusIcon from '@/shared/assets/icons/PlusIcon.svg';
  import TrashIcon from '@/shared/assets/icons/TrashIcon.svg';
  // Composables
  import {
    useDocumentPrefixes,
    useDocumentUsers,
    useDocumentUrlUpload,
    useUrlValidation,
  } from '../model';
  import { urlUploadDataStore } from '../model/useUrlUploadDataStore';
  import { MAX_CHANGELOG_LENGTH } from '@/shared/config';

  interface Props {
    index: number;
    instanceId: number;
    isLast: boolean;
    showRemoveButton?: boolean;
    canAddMore?: boolean;
    hasBatchDuplicatePrefix?: boolean;
    hasDbPrefixConflict?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    showRemoveButton: false,
    canAddMore: true,
    hasBatchDuplicatePrefix: false,
    hasDbPrefixConflict: false,
  });

  const emit = defineEmits<{
    (e: 'add-url'): void;
    (e: 'remove', index: number): void;
    (e: 'valid', isValid: boolean): void;
    (
      e: 'data-update',
      data: {
        url: string;
        prefixId?: number | string;
        prefixCode?: string;
        numbering: number;
        sequential: number;
        changelog: string;
        managerId?: number | string;
      }
    ): void;
  }>();

  const { prefixOptions, selectedPrefix, fetchPrefixes } = useDocumentPrefixes();

  const { userOptions, selectedUser, fetchUsers } = useDocumentUsers({ excludeCurrentUser: false });

  const { urlValue, urlError, isValidUrl } = useUrlValidation();
  const {
    numbering,
    sequential,
    changelog,
    isSubmitting,
    handleNumberingInput,
    handleSequentialInput,
  } = useDocumentUrlUpload();

  const instance = urlUploadDataStore.getOrCreateInstance(props.instanceId);

  urlValue.value = instance.url;
  numbering.value = instance.numbering;
  sequential.value = instance.sequential;
  changelog.value = instance.changelog;

  const incrementNumbering = () => {
    numbering.value++;
    updateStore();
  };

  const decrementNumbering = () => {
    if (numbering.value > 0) {
      numbering.value--;
      updateStore();
    }
  };

  const incrementSequential = () => {
    sequential.value++;
    updateStore();
  };

  const decrementSequential = () => {
    if (sequential.value > 0) {
      sequential.value--;
      updateStore();
    }
  };

  const updateStore = () => {
    const prefixOption = prefixOptions.value.find((opt) => opt.id === selectedPrefix.value);
    const prefixCode = prefixOption?.name || '';

    urlUploadDataStore.updateInstance(props.instanceId, {
      url: urlValue.value,
      prefixId: selectedPrefix.value,
      prefixCode,
      numbering: numbering.value,
      sequential: sequential.value,
      changelog: changelog.value,
      managerId: selectedUser.value,
    });

    emit('data-update', {
      url: urlValue.value,
      prefixId: selectedPrefix.value,
      prefixCode,
      numbering: numbering.value,
      sequential: sequential.value,
      changelog: changelog.value,
      managerId: selectedUser.value,
    });
  };

  const handleRemove = () => {
    urlUploadDataStore.removeInstance(props.instanceId);
    emit('remove', props.index);
  };

  const validateForm = () => {
    const hasPrefix = !!selectedPrefix.value;
    const hasUser = !!selectedUser.value;
    const hasChangelog = changelog.value.trim() && changelog.value.length <= MAX_CHANGELOG_LENGTH;

    const isValid = isValidUrl.value !== false && hasPrefix && hasUser && !!hasChangelog;
    emit('valid', isValid);
  };

  watch(
    [urlValue, selectedPrefix, selectedUser, changelog, numbering, sequential],
    () => {
      updateStore();
      validateForm();
    },
    { immediate: true }
  );

  onMounted(async () => {
    await urlUploadDataStore.initializePrefixes();
    await fetchPrefixes();
    await fetchUsers();
  });
</script>

<template>
  <div
    class="flex flex-col gap-6 text-sm border border-dashed rounded-lg px-6 py-8"
    :class="hasBatchDuplicatePrefix || hasDbPrefixConflict ? 'border-red-16' : 'border-gray-02'"
  >
    <div>
      <div class="flex justify-between">
        <h3 class="text-gray-07 font-semibold mb-2">
          Adicionar documento via URL:
        </h3>
        <IconImage
          v-if="showRemoveButton"
          class="cursor-pointer text-gray-xx-02"
          :src="TrashIcon"
          alt="Remover"
          @click="handleRemove"
        />
      </div>
      <div class="relative">
        <input
          v-model="urlValue"
          type="text"
          :class="[
            'px-4 py-3 pl-10 w-full text-gray-03 rounded-md focus:outline-none border',
            urlError ? 'border-red-16' : 'border-gray-02',
          ]"
          placeholder="Cole aqui a URL do documento"
        >
        <IconImage
          :src="LinkIcon"
          alt="URL"
          class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
        />
      </div>
      <p
        v-if="urlError"
        class="text-red-16 text-sm mt-1"
      >
        {{ urlError }}
      </p>
    </div>

    <div
      id="inputs-wrapper"
      class="flex gap-4"
    >
      <div class="w-fit">
        <label class="block text-gray-07 font-semibold mb-2">Prefixo:</label>
        <SelectInput
          v-model="selectedPrefix"
          :options="prefixOptions"
          label=""
          class="!w-fit"
        />
      </div>

      <div class="w-fit">
        <label class="block text-gray-07 font-semibold mb-2">Numeração:</label>
        <div class="relative">
          <input
            :value="numbering"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            autocomplete="off"
            class="w-23 px-1 py-2 pr-8 border border-gray-02 rounded-md focus:outline-none focus:border-gray-07 text-gray-07 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            @input="handleNumberingInput"
            @blur="numbering = Math.max(0, Math.trunc(numbering))"
          >
          <div class="absolute right-3.5 top-1/2 transform -translate-y-1/2">
            <div class="relative">
              <IconImage
                :src="UpandDownIcon"
                alt="Up and Down arrows"
                class="pointer-events-none"
              />
              <button
                type="button"
                class="absolute top-0 left-0 w-full h-1/2 cursor-pointer"
                @click="incrementNumbering"
              />
              <button
                type="button"
                class="absolute bottom-0 left-0 w-full h-1/2 cursor-pointer"
                @click="decrementNumbering"
              />
            </div>
          </div>
        </div>
      </div>

      <div>
        <label class="block text-gray-07 font-semibold mb-2">Sequencial:</label>
        <div class="relative">
          <input
            :value="sequential"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            autocomplete="off"
            class="w-23 px-1 py-2 pr-8 border border-gray-02 rounded-md focus:outline-none focus:border-gray-07 text-gray-07 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            @input="handleSequentialInput"
            @blur="sequential = Math.max(0, Math.trunc(sequential))"
          >
          <div class="absolute right-3.5 top-1/2 transform -translate-y-1/2">
            <div class="relative">
              <IconImage
                :src="UpandDownIcon"
                alt="Up and Down arrows"
                class="pointer-events-none"
              />
              <button
                type="button"
                class="absolute top-0 left-0 w-full h-1/2 cursor-pointer"
                @click="incrementSequential"
              />
              <button
                type="button"
                class="absolute bottom-0 left-0 w-full h-1/2 cursor-pointer"
                @click="decrementSequential"
              />
            </div>
          </div>
        </div>
      </div>

      <div>
        <label class="block text-gray-07 font-semibold mb-2">Responsável pela aprovação:</label>
        <SelectInput
          v-model="selectedUser"
          :options="userOptions"
          label="Escolha um colaborador"
        />
      </div>
    </div>

    <div>
      <h3 class="text-gray-07 font-medium mb-2">
        Changelog
      </h3>
      <textarea
        v-model="changelog"
        class="px-4 py-3 w-full text-gray-03 rounded-md focus:outline-none border border-gray-02"
        placeholder="Escreva o que mudou no documento"
        :maxlength="MAX_CHANGELOG_LENGTH"
      />
      <span class="text-gray-400 text-xs mt-3">Número máximo de caracteres: 2.000</span>
    </div>
    <div class="flex justify-end">
      <ColoredButton
        v-if="isLast"
        :icon="PlusIcon"
        text="Adicionar URL"
        :loading="isSubmitting"
        :disabled="!canAddMore"
        @click="$emit('add-url')"
      />
    </div>
  </div>
</template>

<style>
  #inputs-wrapper select,
  #inputs-wrapper input {
    height: 44px;
  }
</style>
